/**
 * The Activity log on a record page — the same look on a job, a client, a
 * location and an invoice (docs/majik/PROTOTYPE-DECISIONS.md, rule 9). Two
 * sources, one list, newest first:
 *
 *  - messages and events (`communications`): texts, emails, automatic notices,
 *    "Marked completed", "Payment received";
 *  - changes people made in the app, from the activity log (docs/ACTIVITY.md).
 *
 * Pure: it turns both into entries the screen draws.
 */
import type { RecordChange } from '../history/history.ts'
import { clockAt, shortDay } from '../shared/zoned.ts'
import { type Communication, communicationTitle, partyText } from './communication.ts'
import { canChangeNote, editedLine, isNote, type NoteViewer } from './notes.ts'

/** The colour family of an entry: who it was to or from. */
export const timelineTones = ['customer', 'cleaner', 'billing', 'payment', 'internal'] as const
export type TimelineTone = (typeof timelineTones)[number]

/** Which picture it gets. The screen maps each to an icon. */
export const timelineIcons = [
  'message',
  'mail',
  'check',
  'dollar',
  'plus',
  'edit',
  'user',
  'file',
  'cancel',
  'trash',
  'note',
] as const
export type TimelineIcon = (typeof timelineIcons)[number]

export interface TimelineEntry {
  id: string
  at: string
  /** 'Oct 21 · 9:00 AM' — on the business's clock. */
  whenText: string
  title: string
  /** The pill: "To customer · Georgia", "From QuickBooks", "Internal". */
  party: string
  tone: TimelineTone
  icon: TimelineIcon
  /** The small grey line: what drove it. */
  note: string
  /** How the body is drawn: an outbound or inbound text bubble, or an email. */
  body: { kind: 'text_out' | 'text_in' | 'email'; text: string; subject: string | null } | null
  /** Recorded while Go live is off: it would have gone, and did not. */
  wouldSend: boolean
  /** A text or email: the person it was with, to open that conversation in Messages. */
  contactId?: string
  /**
   * An internal note: its own words (to edit them), whether the person looking
   * may change it, and "Edited Oct 25, 3:40 PM by …" once it has been.
   */
  noteOf?: { id: string; text: string; canChange: boolean; edited: string | null }
}

function whenText(at: string, timeZone: string): string {
  return `${shortDay(at, timeZone)} · ${clockAt(at, timeZone)}`
}

function toneOf(message: Communication): TimelineTone {
  switch (message.party.kind) {
    case 'customer':
      return 'customer'
    case 'cleaner':
      return 'cleaner'
    case 'billing':
      return 'billing'
    case 'quickbooks':
      return message.purpose === 'payment' ? 'payment' : 'billing'
    case 'inspector':
    case 'internal':
      return 'internal'
  }
}

/** The icon an event's title asks for; messages get theirs from the medium. */
const EVENT_ICONS: [RegExp, TimelineIcon][] = [
  [/^payment/i, 'dollar'],
  [/completed/i, 'check'],
  [/^created|added/i, 'plus'],
  [/assigned|contact/i, 'user'],
  [/invoice/i, 'file'],
  [/cancel/i, 'cancel'],
  [/note/i, 'note'],
]

function iconOf(message: Communication): TimelineIcon {
  if (message.channel === 'email') return 'mail'
  if (message.channel === 'sms') return 'message'
  if (message.channel === 'internal_note') return 'note'
  const title = message.title ?? ''
  return EVENT_ICONS.find(([re]) => re.test(title))?.[1] ?? 'edit'
}

/**
 * `viewer` is who is looking, for a note's Edit and Delete; without one, a
 * note reads but can't be changed (an export, a screen that only shows it).
 */
export function timelineFromMessage(
  message: Communication,
  timeZone: string,
  viewer?: NoteViewer,
): TimelineEntry {
  const body =
    message.channel === 'sms' && message.body
      ? {
          kind: message.direction === 'inbound' ? ('text_in' as const) : ('text_out' as const),
          text: message.body,
          subject: null,
        }
      : message.channel === 'email' && message.body
        ? { kind: 'email' as const, text: message.body, subject: message.subject }
        : null
  return {
    id: `m-${message.id}`,
    at: message.at,
    whenText: whenText(message.at, timeZone),
    title: communicationTitle(message),
    party: partyText(message),
    tone: toneOf(message),
    icon: iconOf(message),
    // A note's line is its words and who wrote them; older notes kept both in `note`.
    note:
      isNote(message) && message.author ? `${message.body} — ${message.author.name}` : message.note,
    body,
    wouldSend: message.status === 'would_send',
    ...(body && message.party.contactId ? { contactId: message.party.contactId } : {}),
    ...(isNote(message) && message.author
      ? {
          noteOf: {
            id: message.id,
            text: message.body,
            canChange: viewer ? canChangeNote(message, viewer) : false,
            edited: editedLine(message, timeZone),
          },
        }
      : {}),
  }
}

/** One activity-log change as an entry; `describe` says it in the record's own words. */
export function timelineFromChange(
  change: RecordChange,
  describe: (change: RecordChange) => { title: string; note: string; icon: TimelineIcon },
  timeZone: string,
): TimelineEntry {
  const said = describe(change)
  return {
    id: `c-${change.id}`,
    at: change.at,
    whenText: whenText(change.at, timeZone),
    title: said.title,
    party: 'Internal',
    tone: 'internal',
    icon: said.icon,
    note: said.note,
    body: null,
    wouldSend: false,
  }
}

export function mergeTimeline(...lists: TimelineEntry[][]): TimelineEntry[] {
  return lists.flat().sort((a, b) => b.at.localeCompare(a.at) || b.id.localeCompare(a.id))
}

/**
 * The grey line an entry shows on screen (D-035): "Automatic" for anything an
 * automatic rule sent — the rule is said once, where it is set — and nothing
 * for a text that came in, whose bubble already shows what it was. The export
 * keeps the full note.
 */
export function shownNote(entry: Pick<TimelineEntry, 'note' | 'body' | 'noteOf'>): string {
  if (entry.noteOf) return entry.note
  if (entry.note.startsWith('Automatic')) return 'Automatic'
  if (entry.body?.kind === 'text_in') return ''
  return entry.note
}
